/**
 * MODAL: CREATE FORM
 * Modal pour créer un nouveau formulaire
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Form, FormInput, FormSelect, FormSelectWithCreate, Label, Toggle } from '../ui';
import { Modal, ModalFooter } from '../ui/modal';
import { createFormSchema, type CreateFormInput } from '../../schemas/form';
import { useProductsSelect } from '../../hooks/useProductsSelect';
import { useOrigins } from '../../hooks/useOrigins';
import { CreateOriginModal } from './CreateOriginModal';

interface CreateFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateFormInput) => Promise<void>;
}

export function CreateFormModal({ isOpen, onClose, onSubmit }: CreateFormModalProps) {
  const { products } = useProductsSelect();
  const { origins, createOrigin } = useOrigins();
  const [isCreateOriginModalOpen, setIsCreateOriginModalOpen] = useState(false);

  const form = useForm<CreateFormInput>({
    resolver: zodResolver(createFormSchema),
    defaultValues: {
      name: '',
      slug: '',
      product_id: undefined,
      origin_id: undefined,
      is_active: true,
      generates_bed: false,
    },
  });

  const handleFormSubmit = async (data: CreateFormInput) => {
    try {
      await onSubmit(data);
      form.reset();
      onClose();
    } catch (error) {
      console.error('Erreur lors de la création du formulaire :', error);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const productOptions = products.map((p) => ({
    value: p.id,
    label: p.name,
  }));

  const originOptions = origins.map((origin) => ({
    value: origin.id,
    label: origin.label,
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Créer un formulaire"
      size="lg"
      footer={
        <ModalFooter
          onCancel={handleClose}
          submitLabel="Créer"
          isSubmitting={form.formState.isSubmitting}
          formId="create-form-form"
        />
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleFormSubmit)} id="create-form-form">
          <div className="space-y-6">
            <div className="space-y-4">
              <FormInput
                control={form.control}
                name="name"
                label="Nom du formulaire"
                placeholder="Ex: Inscription 4L Trophy 2026"
                required
              />

              <FormInput
                control={form.control}
                name="slug"
                label="Slug (URL)"
                placeholder="Laissez vide pour générer automatiquement"
              />

              <FormSelect
                control={form.control}
                name="product_id"
                label="Produit associé"
                placeholder="Sélectionnez un produit"
                options={productOptions}
              />

              <FormSelectWithCreate
                control={form.control}
                name="origin_id"
                label="Origine par défaut"
                placeholder="Sélectionnez une origine"
                options={originOptions}
                onCreateClick={() => setIsCreateOriginModalOpen(true)}
                createButtonLabel="Nouvelle"
              />

              {/* Toggle « Générer BED » (solde) — un form BPI se configure
                  ensuite dans le panneau de détail. L'exclusion mutuelle BPI/BED
                  est garantie ici par défaut (generates_bpi non réglé à la création). */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Générer BED</Label>
                  <p className="text-sm text-zinc-500">
                    Génère un bulletin d'engagement définitif (solde) à l'envoi
                  </p>
                </div>
                <Toggle
                  checked={form.watch('generates_bed') ?? false}
                  onChange={(checked) =>
                    form.setValue('generates_bed', checked, { shouldDirty: true })
                  }
                />
              </div>
            </div>
          </div>
        </form>
      </Form>

      <CreateOriginModal
        isOpen={isCreateOriginModalOpen}
        onClose={() => setIsCreateOriginModalOpen(false)}
        onSubmit={async (data) => {
          const newOrigin = await createOrigin(data);
          if (newOrigin) {
            form.setValue('origin_id', newOrigin.id);
          }
        }}
      />
    </Modal>
  );
}
